/**
 * InteractiveTour (session 126p; extended in 126s) — 6-step guided
 * tour. The first 5 steps spotlight in-app DOM elements; the final
 * step (6 of 6) renders a centered modal explaining how to install
 * PeopleiKnow on the user's home screen, with platform-specific copy.
 *
 * Spec:
 *   A1 — auto-complete `tour_taken` companion after all 6 steps viewed
 *
 *   Step 1 — Navigation         (bottom-nav row)
 *   Step 2 — Contact history    (a contact's timeline view)
 *   Step 3 — Groups             (Groups tab)
 *   Step 4 — Locations          (Places map)
 *   Step 5: Meeting people (Import and add contacts)
 *   Step 6 — Add to home screen (no DOM target — modal panel)
 *
 * State machine: each step has a target page + target selector + copy.
 * Advancing to the next step navigates the user to that page first,
 * then mounts the spotlight on the matching DOM element. Closing the
 * tour (Finish or Skip) marks `tour_taken` so the companion check
 * lights up in the onboarding tracker.
 *
 * State persists in sessionStorage so a page reload mid-tour doesn't
 * lose progress (kept lightweight — no localStorage to avoid
 * accidentally re-opening the tour weeks later).
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SpotlightOverlay from './SpotlightOverlay';
import InstallPromptStep from './InstallPromptStep';
import { onboardingAPI } from '../../lib/api';

const STORAGE_KEY = 'pik_active_tour';

const TOUR_STEPS = [
  {
    key: 'navigation',
    title: 'Get around quickly',
    body: 'Use the bottom navigation to jump between Home, Search All, Groups, and Add. Everything you need is one tap away.',
    route: '/',
    target: '[data-testid="bottom-nav"]',
  },
  {
    key: 'contact_history',
    title: 'See your contact history',
    body: 'Search any name and tap a card to open a contact. Their timeline shows the calls, messages, meetings, and notes you\u2019ve logged inside PeopleiKnow.',
    route: '/search',
    target: '[data-testid="search-input"]',
  },
  {
    key: 'groups',
    title: 'Organize people into groups',
    body: 'Tap "Create group" to bundle Family, Coworkers, or Hiking buddies. Groups keep your address book tidy and let you share the right info with the right people.',
    route: '/groups',
    target: '[data-testid="create-group-btn"]',
  },
  {
    key: 'locations',
    title: 'See where your people are',
    body: 'The map plots your contacts by city so you can quickly spot who lives near you, or who to look up when you travel.',
    route: '/places',
    target: '[data-testid="places-map-container"], [data-testid="no-locations-message"]',
  },
  {
    key: 'meeting_people',
    title: 'Capture every meeting with context',
    body: 'Tap Add manually to log a new person along with GPS location and how-we-met details, so you never forget where or why you connected.',
    route: '/import-add',
    target: '[data-testid="import-add-manually"]',
  },
  // Session 126s — final step is a modal, not a spotlight. We keep it
  // in the same array so totalSteps / persisted index logic is shared,
  // but the render path branches on `kind === 'install'`.
  {
    key: 'install_pwa',
    kind: 'install',
    route: '/',
  },
];

export default function InteractiveTour({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [stepIdx, setStepIdx] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? Math.min(parseInt(stored, 10) || 0, TOUR_STEPS.length - 1) : 0;
    } catch {
      return 0;
    }
  });

  const persist = useCallback((idx) => {
    try { sessionStorage.setItem(STORAGE_KEY, String(idx)); } catch {/* ignore */}
  }, []);

  // Navigate to the step's route on activation if we're not there yet.
  useEffect(() => {
    if (!open) return;
    const step = TOUR_STEPS[stepIdx];
    if (!step) return;
    if (location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [open, stepIdx, location.pathname, navigate]);

  const handleNext = async () => {
    if (stepIdx >= TOUR_STEPS.length - 1) {
      // Final step — mark companion done + clear state.
      try {
        await onboardingAPI.markStep('tour_taken');
      } catch {/* don't block close on a network blip */}
      try { sessionStorage.removeItem(STORAGE_KEY); } catch {/* */}
      // Let any listener (e.g. OnboardingProgressTracker) refresh
      // its status so the tour_taken green check lights up. Fire-
      // and-forget — no need to block UI on it.
      window.dispatchEvent(new CustomEvent('pik:tour-completed'));
      onClose?.({ completed: true });
      return;
    }
    const nextIdx = stepIdx + 1;
    persist(nextIdx);
    setStepIdx(nextIdx);
  };

  const handleBack = () => {
    if (stepIdx === 0) return;
    const prevIdx = stepIdx - 1;
    persist(prevIdx);
    setStepIdx(prevIdx);
  };

  const handleSkip = async () => {
    // Skipping still counts as having seen the tour — A1 says auto-
    // complete on full viewing, and "skip" implies the user has had
    // their fill of guidance. We do NOT mark done if they bail at
    // step 1 — that's a hard-quit before they've seen anything.
    if (stepIdx > 0) {
      try { await onboardingAPI.markStep('tour_taken'); } catch {/* */}
      window.dispatchEvent(new CustomEvent('pik:tour-completed'));
    }
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {/* */}
    onClose?.({ completed: false, skipped: true, atStep: stepIdx });
  };

  if (!open) return null;
  const step = TOUR_STEPS[stepIdx];
  if (!step) return null;

  // Final step → modal panel with platform-aware install instructions.
  if (step.kind === 'install') {
    return (
      <InstallPromptStep
        stepNumber={stepIdx + 1}
        totalSteps={TOUR_STEPS.length}
        onBack={handleBack}
        onFinish={handleNext}
        onSkip={handleSkip}
      />
    );
  }

  return (
    <SpotlightOverlay
      target={step.target}
      title={step.title}
      body={step.body}
      stepNumber={stepIdx + 1}
      totalSteps={TOUR_STEPS.length}
      canBack={stepIdx > 0}
      onBack={handleBack}
      onNext={handleNext}
      onSkip={handleSkip}
      finalStep={stepIdx === TOUR_STEPS.length - 1}
    />
  );
}
